import React, { useMemo, useEffect, useState, useRef } from 'react'
import { motion, useSpring, useTransform, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'

const RangeDropdown = ({ options, selected, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const activeLabel = options?.find((o) => o?.key === selected)?.label

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1 transition-colors"
      >
        {activeLabel}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-32 bg-card border border-border rounded-lg shadow-md z-30 py-1">
          {options?.map((option) => (
            <button
              key={option?.key}
              onClick={() => {
                onChange?.(option?.key)
                setOpen(false)
              }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors ${
                option?.key === selected ? "text-primary font-medium" : "text-foreground"
              }`}
            >
              {option?.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * A theme-adaptive metric card: animated counter, fluid-width interactive bar
 * chart (hover tooltip + tap feedback per bar), and an optional built-in
 * date-range dropdown. Requires framer-motion.
 */
export const MinimalStepsCard = ({
  title,
  subtitle,
  totalSteps,
  stepsUnit = 'steps',
  icon,
  data = [],
  className,
  rangeOptions,
  selectedRange,
  onRangeChange,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const maxValue = useMemo(() => Math.max(...data.map((d) => d.value), 1), [data])

  const animatedSteps = useSpring(0, { mass: 0.8, stiffness: 75, damping: 15 })
  const displaySteps = useTransform(animatedSteps, (v) => new Intl.NumberFormat('en-US').format(Math.round(v)))

  useEffect(() => {
    animatedSteps.set(totalSteps)
  }, [animatedSteps, totalSteps])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.02 } },
  }

  const barVariants = {
    hidden: { height: '0%' },
    visible: (customHeight) => ({
      height: customHeight,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    }),
  }

  const handleBarTap = () => {
    if (typeof window !== 'undefined' && window?.navigator?.vibrate) {
      window.navigator.vibrate(8)
    }
  }

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col rounded-2xl border bg-card p-5 sm:p-6 text-card-foreground',
        className
      )}
      role="figure"
      aria-label={`${title} for ${subtitle}: ${totalSteps} ${stepsUnit}`}
    >
      {/* Header Section */}
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold truncate">{title}</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rangeOptions && (
            <RangeDropdown options={rangeOptions} selected={selectedRange} onChange={onRangeChange} />
          )}
          {icon && (
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-muted shrink-0">
              {icon}
            </div>
          )}
        </div>
      </header>

      {/* Main Metric */}
      <div className="my-6 sm:my-8" aria-live="polite">
        <motion.span className="text-4xl sm:text-5xl font-bold tracking-tight">{displaySteps}</motion.span>
        <span className="ml-2 text-lg sm:text-xl text-muted-foreground">{stepsUnit}</span>
      </div>

      {/* Bar Chart Visualization — interactive: hover tooltip, tap feedback */}
      <motion.div
        className="relative mt-auto flex h-28 sm:h-32 w-full items-end gap-1"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        aria-label={`${title} chart`}
      >
        {data.map((item, index) => {
          const barHeight = `${(item.value / maxValue) * 100}%`
          const isHovered = hoveredIndex === index
          return (
            <div
              key={index}
              className="relative h-full w-full cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onTouchStart={() => {
                setHoveredIndex(index)
                handleBarTap()
              }}
              aria-label={`${item?.label || `Point ${index + 1}`}: ${item.value}`}
            >
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.12 }}
                    className="absolute -top-8 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] font-medium px-2 py-1 shadow-sm pointer-events-none"
                  >
                    {new Intl.NumberFormat('en-US').format(item.value)}
                    {item?.label ? ` · ${item.label}` : ''}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Background/Track for the bar */}
              <div className="absolute bottom-0 h-full w-full rounded-t-sm bg-muted/30" />

              {/* Animated fill element — springs in on mount, bumps on hover/tap */}
              <motion.div
                className="absolute bottom-0 w-full rounded-t-sm bg-card-foreground origin-bottom"
                variants={barVariants}
                custom={barHeight}
                whileHover={{ scaleY: 1.04, opacity: 0.85 }}
                whileTap={{ scaleY: 0.96 }}
                transition={{ scaleY: { type: 'spring', stiffness: 300, damping: 20 } }}
              />
            </div>
          )
        })}
      </motion.div>
    </div>
  )
}

export default MinimalStepsCard